import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
	Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-6";
import { AdapterDateFns } from "@mui/x-date-pickers-6/AdapterDateFns";
import { addDays, differenceInDays, format } from "date-fns";
import React, { useState } from "react";

import { productionPlanApi } from "../../../../services/production-plan-api";
// eslint-disable-next-line sort-imports
import { GhostButton,planner,PrimaryButton } from "../../planner-ui"; // eslint-disable-line sort-imports
import { ManufacturingOrder } from "../utils/calculations";

interface CopyMOFormProps {
	open: boolean;
	onClose: () => void;
	onCopySuccess: () => void;
	sourceMO: ManufacturingOrder | null;
}

const CopyMOForm: React.FC<CopyMOFormProps> = ({
	open,
	onClose,
	onCopySuccess,
	sourceMO,
}) => {
	const [targetDate, setTargetDate] = useState<Date | null>(new Date());
	const [copies, setCopies] = useState(1);
	const [loading, setLoading] = useState(false);

	const handleCopy = async () => {
		if (!sourceMO || !targetDate) return;

		setLoading(true);
		try {
			const sourceStart = new Date(sourceMO.startTime);
			const daysDiff = differenceInDays(targetDate, sourceStart);

			for (let i = 0; i < copies; i++) {
				const newStart = addDays(new Date(sourceMO.startTime), daysDiff + i);
				const newEnd = sourceMO.endTime
					? addDays(new Date(sourceMO.endTime), daysDiff + i)
					: undefined;

				await productionPlanApi.create({
					productId: sourceMO.productId,
					sku: sourceMO.sku,
					quantity: sourceMO.quantity,
					workstation: sourceMO.workstation,
					startTime: newStart.toISOString(),
					endTime: newEnd?.toISOString(),
					laborStandardId: sourceMO.laborStandardId,
				});
			}

			onCopySuccess();
		} catch (error) {
			console.error("Failed to copy MO:", error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			PaperProps={{
				sx: { borderRadius: `${planner.radius}px`, boxShadow: planner.shadowDialog },
			}}
		>
			<DialogTitle sx={{ ...planner.type.title, color: planner.navy }}>
				Copy job order
				<IconButton
					onClick={onClose}
					sx={{ position: "absolute", right: 8, top: 8, color: planner.inkMuted }}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers>
				{sourceMO && (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
						<Typography sx={{ ...planner.type.body, color: planner.inkSecondary }}>
							Copying: <strong>{sourceMO.productName || sourceMO.sku}</strong> on{" "}
							{sourceMO.workstation}
						</Typography>

						<LocalizationProvider dateAdapter={AdapterDateFns}>
							<DatePicker
								label="Target Date"
								value={targetDate}
								onChange={(newValue) => setTargetDate(newValue)}
							/>
						</LocalizationProvider>

						<TextField
							label="Number of Copies"
							type="number"
							value={copies}
							onChange={(e) => setCopies(Math.max(1, parseInt(e.target.value) || 1))}
							inputProps={{ min: 1, max: 30 }}
							helperText="Creates consecutive copies starting from target date"
						/>
					</Box>
				)}
			</DialogContent>
			<DialogActions sx={{ borderTop: `${planner.borderWidth}px solid ${planner.hairline}`, px: 3, py: 2 }}>
				<GhostButton size="small" onClick={onClose}>Cancel</GhostButton>
				<PrimaryButton
					size="small"
					onClick={handleCopy}
					disabled={loading || !targetDate}
					startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
				>
					{loading ? "Copying..." : `Copy ${copies} time${copies > 1 ? "s" : ""}`}
				</PrimaryButton>
			</DialogActions>
		</Dialog>
	);
};

export default CopyMOForm;
